import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CalendarDays, CheckCircle2, Clock, Loader2, MapPin, XCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Row = ({ icon: Icon, label, value }) =>
  value ? (
    <div className="flex items-start gap-3 border-b border-zinc-100 py-3 last:border-0">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-zinc-400" />
      <div className="min-w-0">
        <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-400">
          {label}
        </div>
        <div className="text-base font-medium text-zinc-900">{value}</div>
      </div>
    </div>
  ) : null;

export default function ProposalScreen() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [sending, setSending] = useState("");
  const [done, setDone] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/proposals/${token}`);
      setData(res.data);
      if (res.data.status !== "enviada") setDone(res.data.status);
    } catch (e) {
      setError(
        e?.response?.data?.detail || "Esta propuesta ya no está disponible."
      );
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const respond = async (answer) => {
    setSending(answer);
    try {
      const res = await axios.post(`${API}/proposals/${token}/respond`, { answer });
      setDone(res.data.status);
    } catch (e) {
      setError(e?.response?.data?.detail || "No se pudo registrar tu respuesta.");
    } finally {
      setSending("");
    }
  };

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-zinc-50 px-6">
        <div className="w-full max-w-sm text-center" data-testid="proposal-error">
          <XCircle className="mx-auto mb-4 h-10 w-10 text-zinc-400" />
          <p className="text-base font-medium text-zinc-800">{error}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-zinc-50">
        <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
      </div>
    );
  }

  const c = data.course;
  const tipo = { inicial: "Inicial", ppaa: "Primeros Auxilios" }[c.course_type] || "Reciclaje";

  return (
    <div className="min-h-screen bg-zinc-50 px-5 py-10" data-testid="proposal-screen">
      <div className="mx-auto w-full max-w-md">
        <img
          src="/desfiemer_logo.png"
          alt="Desfiemer"
          className="mb-8 h-8 w-auto select-none"
          draggable={false}
        />

        {done ? (
          <div
            className={`rounded-lg border-2 p-6 text-center ${
              done === "aceptada"
                ? "border-emerald-500 bg-emerald-50"
                : "border-zinc-300 bg-white"
            }`}
            data-testid="proposal-result"
          >
            {done === "aceptada" ? (
              <CheckCircle2 className="mx-auto mb-3 h-12 w-12 text-emerald-600" />
            ) : (
              <XCircle className="mx-auto mb-3 h-12 w-12 text-zinc-400" />
            )}
            <h1 className="heading-font text-xl font-extrabold text-zinc-950">
              {done === "aceptada"
                ? "¡Formación aceptada!"
                : "Respuesta registrada"}
            </h1>
            <p className="mt-2 text-sm text-zinc-600">
              {done === "aceptada"
                ? "Queda asignada a tu nombre."
                : "Hemos anotado que no estás disponible. Gracias por responder."}
            </p>
          </div>
        ) : (
          <>
            <p className="text-sm font-medium capitalize text-zinc-500">
              Hola {(data.instructor_name || "").split(" ")[0].toLowerCase()},
            </p>
            <h1 className="heading-font mt-1 text-2xl font-extrabold leading-tight text-zinc-950">
              ¿Puedes impartir esta formación?
            </h1>

            <div className="mt-6 rounded-lg border-2 border-zinc-200 bg-white px-5 py-1">
              <Row icon={MapPin} label="Cliente" value={c.client_name} />
              <Row icon={MapPin} label="Lugar" value={c.location} />
              <Row icon={CalendarDays} label="Fecha" value={c.dates} />
              <Row icon={Clock} label="Horario" value={c.schedule} />
              <Row
                icon={Clock}
                label="Tipo de curso"
                value={`${tipo}${c.hours ? ` · ${Math.round(c.hours)} h` : ""}${
                  c.days > 1 ? ` · ${c.days} días` : ""
                }`}
              />
              <Row
                icon={MapPin}
                label="Distancia desde tu domicilio"
                value={
                  c.km_one_way ? `${Math.round(c.km_one_way)} km (solo ida)` : ""
                }
              />
            </div>

            {data.already_covered && (
              <p
                className="mt-4 rounded-md border-2 border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
                data-testid="proposal-covered"
              >
                Esta formación ya está cubierta por otro instructor.
              </p>
            )}

            <div className="mt-6 space-y-3">
              <button
                type="button"
                onClick={() => respond("accept")}
                disabled={!!sending || data.already_covered}
                className="flex h-14 w-full items-center justify-center gap-2 rounded-md bg-emerald-600 text-base font-bold text-white transition-colors hover:bg-emerald-700 disabled:opacity-50"
                data-testid="proposal-accept"
              >
                {sending === "accept" ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <CheckCircle2 className="h-5 w-5" />
                )}
                Aceptar formación
              </button>
              <button
                type="button"
                onClick={() => respond("decline")}
                disabled={!!sending}
                className="flex h-14 w-full items-center justify-center gap-2 rounded-md border-2 border-zinc-300 bg-white text-base font-bold text-zinc-700 transition-colors hover:border-zinc-500 disabled:opacity-50"
                data-testid="proposal-decline"
              >
                {sending === "decline" ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <XCircle className="h-5 w-5" />
                )}
                No disponible
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
